import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { WATCH_PHOTOS } from "./watchScripts";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { useDemoTracking } from "../lib/analytics";
import { MASTER_ENABLER_STEPS, MasterEnablerPane, MASTER_ENABLER_SECTION } from "./masterEnabler";
import { useNarrator, fetchNarration } from "../lib/narration";
import { Hand, Play, Pause, SkipBack, SkipForward, RotateCcw, Plus, Download, FileSpreadsheet,
  Cpu, Server, Users, FolderPlus, HandCoins, Wallet, Coins, Landmark, PiggyBank, Bitcoin,
  Percent, ArrowLeftRight, CalendarClock, Share2, Boxes, Link2, Unlink, HardDriveDownload,
  Upload, FileText, MonitorSmartphone, Layers, Volume2, VolumeX, Loader2, Scale, Paperclip, Gauge, GitBranch } from "lucide-react";

// ============================================================================
// FULL SUITE WATCH DEMO — a single, continuous cross-app storyline that walks
// Office Sync -> Familigi -> BoxAlong -> Currencida -> SEBAA -> Office Sync.
// FRONTEND-ONLY narration for now.
// TODO(backend): replicate these steps into the backend manual source and warm
// translations for all 15 languages (see /site/manual-steps + manual_content.py).
// ============================================================================

const BRAND = { holdings: "BoxAlong™", family_tree: "Familigi™", currency: "Currencida™", benefits: "SEBAA™", master_enabler: "Master Enabler" };

// Fixed rail order for the "flight path" animation across the suite.
const RAIL = [
  { key: "family_tree", short: "Familigi™", icon: Users },
  { key: "holdings", short: "BoxAlong™", icon: Boxes },
  { key: "currency", short: "Currencida™", icon: Coins },
  { key: "benefits", short: "SEBAA™", icon: HandCoins },
];

const OFFICE_RIDS = ["fs-cluster-simple", "fs-cluster-full", "fs-cache-accept", "fs-cache-core",
  "fs-report", "fs-send-machine", "fs-disengage", "fs-unpair"];

const ME_SUITE_STEPS = MASTER_ENABLER_STEPS.map((m) => ({ s: MASTER_ENABLER_SECTION, app: "master_enabler", rid: m.rid, h: m.h, d: m.d, silent: m.silent, dur: m.dur }));
const OB = ME_SUITE_STEPS.length;

const SCRIPT = [
  ...ME_SUITE_STEPS,
  // Section A — Office Sync: connect & seed
  { s: "Office Sync — connect & seed", app: "holdings", rid: "fs-cluster-simple",
    h: "Enable the simple cluster function on BoxAlong™",
    d: "Turn on the simple cluster to pair with another BoxAlong™ app (or the full FopFo™ suite)." },
  { s: "Office Sync — connect & seed", app: "holdings", rid: "fs-cache-accept",
    h: "Accept the 6-month dividend fund into the cluster cache (HKD)",
    d: "Accept, to cache, the 6-month record of a group dividend-investing fund in HKD (currency C) — kept in HKD." },
  { s: "Office Sync — connect & seed", app: "holdings", rid: "fs-cache-core",
    h: "Download from cache into Core",
    d: "Pull the cached records down into this app's core store." },
  { s: "Office Sync — connect & seed", app: "holdings", rid: "fs-cluster-full",
    h: "Switch to the full cluster (all 4 apps)",
    d: "Enable the full cluster so Familigi™, BoxAlong™, Currencida™ and SEBAA™ share one office — if not already done." },
  // Section B — Familigi
  { s: "Familigi — build the family", app: "family_tree", rid: "fs-creategroup",
    h: "Create 3 groups from the family tree",
    d: "Select names or photos to form: 40 Seniors (65+), 60 Adults (21–64), and 100 Children (1 day–20)." },
  // Section C — BoxAlong
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-add",
    h: "Set up a Monthly Term Deposit interest — USD 25k (currency A)",
    d: "Add a term-deposit holding paying USD 25,000 monthly interest." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-add",
    h: "Set up a net rental income — GBP 20k (currency B)",
    d: "Add a rental-income holding paying GBP 20,000 net." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-rates",
    h: "Set a conversion rate to CAD (×1.8)",
    d: "Define the CAD conversion rate used when income flows between pools." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-tax",
    h: "Add 10% withholding tax on GBP only",
    d: "Apply a 10% withholding to the GBP rental stream; USD is untouched." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-topup",
    h: "GBP pays into & tops up the CAD account on the 15th",
    d: "Schedule GBP income to fund and top up the CAD account every 15th of the month." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-topup",
    h: "USD pays into & tops up the US account on the 15th",
    d: "Schedule USD interest to fund and top up the US account every 15th of the month." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-rates",
    h: "Share a secure Principal / Advisor Portal",
    d: "From BoxAlong™'s Entities & Ownership Consolidation you can publish a read-only, no-login portal of the governed net-worth roll-up — white-labelled with the family name and (Platinum and up) your own logo, with a who-opened view log, a new-device alert, an auto-expiring and rotatable link, and an optional weekly/monthly/quarterly snapshot email." },
  { s: "BoxAlong — holdings & income", app: "holdings", rid: "fs-topup",
    h: "Upgrade support as a monthly subscription",
    d: "Paid Support Tiers (Platinum, Rhodium, Osmium) are true monthly Stripe subscriptions — they auto-renew, unlock more AI Statement Reader parses, and include a Manage-billing portal to update the card or cancel anytime." },
  // Section D — Currencida
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-pay-benefit",
    h: "Pay the CAD benefit from the CAD account (2nd & 17th, 15% tax)",
    d: "Pay a CAD benefit from the CAD account on the 2nd and 17th of each month, withholding 15% tax." },
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-pay-adults",
    h: "Pay 25% of the USD balance to Adults",
    d: "Distribute 25% of the current USD pool balance to the Adults group." },
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-crypto",
    h: "Move 30% of USD into 2 cryptocurrencies (BTC, USDT)",
    d: "Convert 30% of the USD balance evenly into BTC and USDT pools." },
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-gold",
    h: "Use 15% of USD to buy gold",
    d: "Convert 15% of the USD balance into a gold holding." },
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-savings",
    h: "Put 20% into a high-interest savings account (5%)",
    d: "Move 20% of the USD balance into a 5% high-interest savings pool." },
  { s: "Currencida — allocate & pay", app: "currency", rid: "fs-leave",
    h: "Leave the last 10% of USD in the account",
    d: "Retain the remaining 10% of the USD balance as working cash." },
  // Section E — SEBAA
  { s: "SEBAA — distribute benefits", app: "benefits", rid: "fs-auto-pay",
    h: "Automate the CAD benefit (2nd & 22nd, 15% tax)",
    d: "Set the CAD benefit to pay automatically from the CAD account on the 2nd and 22nd, withholding 15%." },
  { s: "SEBAA — distribute benefits", app: "benefits", rid: "fs-pay-classes",
    h: "Pay 3 classes of people",
    d: "Seniors 50% — 40 people · Young adults 30% — 60 people · Children 20% — 100 (via parents/guardians)." },
  { s: "SEBAA — distribute benefits", app: "benefits", rid: "fs-equal-split",
    h: "Divide equally within each group",
    d: "Split each class's allocation equally across its members." },
  { s: "SEBAA — distribute benefits", app: "benefits", rid: "fs-share-report",
    h: "Share the 6-month distribution report in cluster mode",
    d: "Share a 6-month distribution report over the cluster to a visitor laptop/desktop on the same network." },
  // Section F — Office Sync: report & disconnect
  { s: "Office Sync — report & disconnect", app: "holdings", rid: "fs-cluster-simple",
    h: "Re-enable the simple cluster on BoxAlong™",
    d: "Bring up the simple cluster again to another BoxAlong™ app or the full suite." },
  { s: "Office Sync — report & disconnect", app: "holdings", rid: "fs-report",
    h: "Create a report and send it to the cache",
    d: "Generate the report and place it in the shared cluster cache." },
  { s: "Office Sync — report & disconnect", app: "holdings", rid: "fs-send-machine",
    h: "Send from cache to the recipient machine",
    d: "Push the cached report from the cache to the paired recipient machine." },
  { s: "Office Sync — report & disconnect", app: "holdings", rid: "fs-disengage",
    h: "Disengage the cluster connection (single app)",
    d: "Close the cluster link when running a single app." },
  { s: "Office Sync — report & disconnect", app: "holdings", rid: "fs-unpair",
    h: "Unpair from the external machine (full suite)",
    d: "Unpair the external machine when running the full suite." },
  // Section G — Consolidate: import & net-worth
  { s: "Consolidate — import & net-worth", app: "family_tree", rid: "fs-import-people",
    h: "Import people into Familigi™ from a spreadsheet",
    d: "Open a tree and click Import in the toolbar. Pick a CSV or Excel file (First name, Last name, Gender, Groups); the columns auto-match, you preview, and everyone is added to the open tree in one pass." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-import-holdings",
    h: "Import holdings into BoxAlong™ from a spreadsheet",
    d: "On Holdings, click Import CSV/Excel, map Item name / Category / Gross value / Status, preview and import — no manual re-keying." },
  { s: "Consolidate — import & net-worth", app: "currency", rid: "fs-import-currency",
    h: "Import currency pools & benefits from a spreadsheet",
    d: "Currencida™ and SEBAA™ each have the same Import CSV/Excel button, so you can bulk-load pools and benefits the same way." },
  { s: "Consolidate — import & net-worth", app: "currency", rid: "fs-liabilities",
    h: "Record what the estate owes in Currencida™",
    d: "Open Liabilities on the Currency page and add mortgages, loans or facilities (or import them). Each one is subtracted from your assets." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-networth",
    h: "Download the Consolidated Net-Worth Statement",
    d: "On Holdings, click Net-worth statement (PDF): held holdings by category + currency pools (converted at each rate) − liabilities = net worth, in one branded document." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-export",
    h: "Export a copy to a file (CSV or JSON)",
    d: "Prefer a file over cluster sync? Click Export on any module and pick CSV (spreadsheet) or JSON (portable snapshot). Familigi™, BoxAlong™, Currencida™ (pools + liabilities) and SEBAA™ each export in one click — the copy downloads to your own device." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-attach",
    h: "Attach photos & documents to an asset",
    d: "On a Mobility or Real Estate holding (and on any Familigi™ person), the paperclip opens a gallery: add photos and PDF appraisals, receipts or titles. Images are re-encoded to strip metadata and every file stays private to the account." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-appraisal",
    h: "Track an asset's appraised value over time",
    d: "The gauge button on any holding opens a dated FMV history (date, value, source) plus a Replacement/Insurance value. The newest appraisal becomes the asset's current value — shown with a mini trend — and the Net-Worth Statement uses it in place of gross value." },
  { s: "Consolidate — import & net-worth", app: "holdings", rid: "fs-capital",
    h: "Track a fund's committed vs. called capital",
    d: "On a private fund the piggy-bank button records the Total commitment plus dated capital calls and distributions, showing Called-to-date, Remaining and a % called bar. Honest arithmetic only — not a reconciliation or waterfall engine and no custodian feed." },
  { s: "Consolidate — import & net-worth", app: "family_tree", rid: "fs-gedcom",
    h: "Move a tree in or out with GEDCOM",
    d: "Familigi™ imports a GEDCOM (.ged) from Ancestry, MyHeritage or FamilySearch into a brand-new tree, and exports any open tree back out as GEDCOM — names, dates and parent/spouse links travel with it." },
  // Section H — Reports & Compliance (admin-owned; runner-facing tools shown too)
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "holdings", rid: "fs-import-ofx",
    h: "Import a bank statement in OFX / QFX",
    d: "The Import CSV/Excel button on Currencida™ and BoxAlong™ now also accepts .ofx and .qfx — the standard bank/brokerage statement formats. Pick the file, preview and import, just like a spreadsheet." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "holdings", rid: "fs-twr",
    h: "Read the Portfolio TWR tile on the Wealth Dashboard",
    d: "The Wealth Dashboard's Portfolio TWR tile shows a Modified-Dietz Time-Weighted Return that removes the distorting effect of contribution/withdrawal timing, so returns are comparable across periods." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "holdings", rid: "fs-regbundle",
    h: "Download a Regulatory Bundle (ZIP)",
    d: "The Data toolbar's Regulatory bundle button packages your Holdings, Currency, Liabilities, Persons, Benefits and Entities as XML + JSON with a SHA-256 MANIFEST for offline audit, plus an optional recipient-watermarked Net-Worth Statement PDF." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "master_enabler", rid: "fs-compliance-cal",
    h: "Open the Compliance Calendar (Master Enabler)",
    d: "Reports & Compliance surfaces 14 curated filings across 11 jurisdictions (US-SEC, US-FinCEN, US-IRS, CA-CRA, UK-HMRC, EU-ESMA, AU-ATO, CH-EStV, IN-ITAT, SG-IRAS, Internal) with days-until, cadence and authority per row." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "master_enabler", rid: "fs-delegate",
    h: "Delegate a filing to a named runner",
    d: "Assign a name + email (role hint and notes optional). Automated nag emails fire at 30 days, 14 days and 3 days before the due date; a new cycle resets the counter." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "master_enabler", rid: "fs-board-snapshot",
    h: "Generate a Board-Ready Snapshot",
    d: "A single ZIP with the full regulatory bundle, a machine-readable metrics.json, and a branded one-page executive cover-letter PDF. The ZIP is Ed25519-signed over a deterministic content MANIFEST and RFC-3161-timestamped via freetsa.org (with graceful degradation)." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "master_enabler", rid: "fs-hash-lookup",
    h: "Trace a leaked PDF via its 12-character watermark",
    d: "Every PDF carries a diagonal 12-character viewer hash + timestamp. Drop it into the Hash lookup panel and Reports & Compliance returns the recipient and the exact first-download time." },
  { s: "Reports & Compliance — bundles, calendar, board snapshot & verify", app: "master_enabler", rid: "fs-verify",
    h: "Verify any bundle on the public /verify page",
    d: "Regulators or board members visit /verify (no login), pick the ZIP they received, and the page rehashes every file against MANIFEST.txt and verifies the Ed25519 SIGNATURE.txt against the vendor's public key (GET /api/public-key)." },
];

const Pill = ({ rid, icon: Icon, label, tone }) => (
  <span data-rid={rid} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${
    tone === "blue" ? "border-transparent bg-[var(--primary)] text-white"
    : tone === "danger" ? "border-[#DA1E28]/40 text-[#DA1E28]"
    : "border-black/15 dark:border-white/15"}`}><Icon size={11} /> {label}</span>
);

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-black/10 bg-white text-black shadow-lg dark:border-white/10 dark:bg-neutral-900 dark:text-white">
    <div className="flex items-center gap-2 border-b border-black/10 px-3 py-2 dark:border-white/10">
      <span className="font-head text-sm font-semibold">{title}</span>
      <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600">Cluster · Active</span>
    </div>
    {children}
  </div>
);

const Rows = ({ cols, rows }) => (
  <div className="mx-3 mt-2 overflow-hidden rounded-md border border-black/10 dark:border-white/10">
    <table className="w-full text-[11px]">
      <thead><tr className="border-b border-black/10 dark:border-white/10">{cols.map((c) => <th key={c} className="p-2 text-start font-medium opacity-60">{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-black/5 last:border-0 dark:border-white/5">{r.map((c, j) => <td key={j} className="p-2">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

function HoldingsPane({ office }) {
  const dim = office ? "scale-[0.97] opacity-40 blur-[1.5px]" : "opacity-100";
  return (
    <Card title="BoxAlong™ — Holdings & Office Sync">
      <div className={`origin-top transition-all duration-700 ${dim}`}>
        <div className="flex flex-wrap items-center gap-1 px-3 pt-2">
          <Pill rid="fs-manual" icon={Download} label="Manual" />
          <Pill rid="fs-csv" icon={FileSpreadsheet} label="CSV" />
          <Pill rid="fs-export" icon={Download} label="Export CSV/JSON" />
          <Pill rid="fs-attach" icon={Paperclip} label="Photos & docs" />
          <Pill rid="fs-appraisal" icon={Gauge} label="FMV history" />
          <Pill rid="fs-capital" icon={PiggyBank} label="Capital calls" />
          <Pill rid="fs-import-holdings" icon={Upload} label="Import CSV/Excel" />
          <Pill rid="fs-networth" icon={FileText} label="Net-worth PDF" />
          <Pill rid="fs-rates" icon={ArrowLeftRight} label="Rates" />
          <Pill rid="fs-tax" icon={Percent} label="Tax" />
          <Pill rid="fs-topup" icon={CalendarClock} label="Top-up schedule" />
          <Pill rid="fs-add" icon={Plus} label="Add" tone="blue" />
        </div>
        <div data-rid="fs-automation" className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-black/10 bg-black/[0.02] px-3 py-1.5 text-[11px] dark:border-white/10 dark:bg-white/[0.03]">
          <Cpu size={13} className="text-[var(--primary)]" /> FopFo Automation · Draw-down · Cross-app top-ups
        </div>
        <Rows cols={["Holding", "Currency", "Value / income", "Schedule"]} rows={[
          ["Monthly term deposit", "USD (A)", "USD 25,000 / mo", "15th → US acct"],
          ["Net rental income", "GBP (B)", "GBP 20,000 net", "15th → CAD acct"],
          ["Group dividend fund", "HKD (C)", "6-month record", "held in HKD"],
        ]} />
      </div>
      {/* Office Sync / Cluster panel */}
      <div style={office ? { backgroundColor: "color-mix(in srgb, var(--primary) 8%, transparent)" } : undefined}
        className={`m-3 rounded-md border p-2 transition-all duration-700 ${office ? "scale-[1.02] border-[var(--primary)] shadow-lg" : "border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.03]"}`}>
        <div className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-55"><Server size={11} className="text-[var(--primary)]" /> Office Sync · Cluster</div>
        <div className="flex flex-wrap items-center gap-1">
          <Pill rid="fs-cluster-simple" icon={Link2} label="Cluster: Simple" />
          <Pill rid="fs-cluster-full" icon={Boxes} label="Cluster: Full (4 apps)" />
          <Pill rid="fs-cache-accept" icon={Upload} label="Accept to cache" />
          <Pill rid="fs-cache-core" icon={HardDriveDownload} label="Cache → Core" />
          <Pill rid="fs-report" icon={FileText} label="Create report" />
          <Pill rid="fs-send-machine" icon={MonitorSmartphone} label="Send to machine" />
          <Pill rid="fs-disengage" icon={Unlink} label="Disengage" tone="danger" />
          <Pill rid="fs-unpair" icon={Unlink} label="Unpair" tone="danger" />
        </div>
      </div>
    </Card>
  );
}

function FamiligiPane() {
  const groups = [
    { n: "Seniors (65+)", c: 40, p: [9, 12, 13] },
    { n: "Adults (21–64)", c: 60, p: [2, 5, 6] },
    { n: "Children (1d–20)", c: 100, p: [8, 4, 7] },
  ];
  return (
    <Card title="Familigi™ — Family Tree">
      <div className="flex flex-wrap items-center gap-1 px-3 pt-2">
        <Pill rid="fs-groups" icon={Users} label="Groups" />
        <Pill rid="fs-import-people" icon={Upload} label="Import" />
        <Pill rid="fs-creategroup" icon={FolderPlus} label="Create group" tone="blue" />
        <Pill rid="fs-gedcom" icon={GitBranch} label="GEDCOM" />
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        {groups.map((g, k) => (
          <div key={k} className="rounded-lg border border-black/10 p-2 dark:border-white/10">
            <div className="mb-1 flex -space-x-2">{g.p.map((p, i) => <img key={i} src={WATCH_PHOTOS[p]} alt="" className="h-6 w-6 rounded-full object-cover ring-2 ring-white dark:ring-neutral-900" />)}</div>
            <div className="text-[11px] font-medium">{g.n}</div>
            <div className="text-[10px] opacity-50">{g.c} people</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CurrencidaPane() {
  return (
    <Card title="Currencida™ — Currency">
      <div className="flex flex-wrap items-center gap-1 px-3 pt-2">
        <Pill rid="fs-manual2" icon={Download} label="Manual" />
        <Pill rid="fs-import-currency" icon={Upload} label="Import CSV/Excel" />
        <Pill rid="fs-liabilities" icon={Scale} label="Liabilities" />
        <Pill rid="fs-topup2" icon={Wallet} label="Auto top-up" />
        <Pill rid="fs-add" icon={Plus} label="Add pool" tone="blue" />
      </div>
      <Rows cols={["Pool", "Balance", "Rate→base"]} rows={[
        ["USD", "1,000,000", "1"],
        ["CAD", "540,000", "0.56"],
        ["GBP", "20,000", "1.27"],
      ]} />
      <div className="mx-3 mt-2 rounded-md border border-[#8a1c1c]/25 bg-[#8a1c1c]/[0.04] p-2">
        <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#8a1c1c]/80"><Scale size={11} /> Liabilities (subtracted from assets)</div>
        <table className="w-full text-[11px]"><tbody>
          <tr className="border-b border-black/5 last:border-0 dark:border-white/5"><td className="p-1.5">Mortgage</td><td className="p-1.5 text-end">500,000</td></tr>
          <tr><td className="p-1.5">Car loan</td><td className="p-1.5 text-end">25,000</td></tr>
        </tbody></table>
      </div>
      <div className="m-3 rounded-md border border-black/10 bg-black/[0.02] p-2 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-55">Allocate the USD pool</div>
        <div className="flex flex-wrap items-center gap-1">
          <Pill rid="fs-pay-benefit" icon={HandCoins} label="Pay CAD benefit (2nd/17th)" />
          <Pill rid="fs-pay-adults" icon={Users} label="25% → Adults" />
          <Pill rid="fs-crypto" icon={Bitcoin} label="30% → BTC / USDT" />
          <Pill rid="fs-gold" icon={Coins} label="15% → Gold" />
          <Pill rid="fs-savings" icon={PiggyBank} label="20% → Savings 5%" />
          <Pill rid="fs-leave" icon={Landmark} label="Leave 10%" />
        </div>
      </div>
    </Card>
  );
}

function SebaaPane() {
  return (
    <Card title="SEBAA™ — Benefits">
      <div className="flex flex-wrap items-center gap-1 px-3 pt-2">
        <Pill rid="fs-manual3" icon={Download} label="Manual" />
        <Pill rid="fs-add" icon={Plus} label="Add benefit" tone="blue" />
      </div>
      <Rows cols={["Benefit", "Fund pool", "Withholding"]} rows={[
        ["CAD benefit", "CAD account", "15%"],
      ]} />
      <div className="m-3 rounded-md border border-black/10 bg-black/[0.02] p-2 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-55">Distribution</div>
        <div className="flex flex-wrap items-center gap-1">
          <Pill rid="fs-auto-pay" icon={CalendarClock} label="Auto-pay (2nd/22nd)" />
          <Pill rid="fs-pay-classes" icon={Users} label="Pay 3 classes" />
          <Pill rid="fs-equal-split" icon={Percent} label="Equal split" />
          <Pill rid="fs-share-report" icon={Share2} label="Share report (cluster)" />
        </div>
      </div>
    </Card>
  );
}

function PaneFor({ app, office, step }) {
  if (app === "master_enabler") return <MasterEnablerPane activeRid={step?.rid} active={step} />;
  if (app === "holdings") return <HoldingsPane office={office} />;
  if (app === "family_tree") return <FamiligiPane />;
  if (app === "currency") return <CurrencidaPane />;
  if (app === "benefits") return <SebaaPane />;
  return null;
}

export default function FullSuiteTour() {
  const { lang } = useApp();
  const [sp] = useSearchParams();
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [voiceOn, setVoiceOn] = useState(sp.get("voice") === "1");
  const [box, setBox] = useState(null);
  const [i18n, setI18n] = useState(null);
  const wrapRef = useRef(null);
  const activeRef = useRef(null);
  useDemoTracking("suite", lang, i, SCRIPT.length);

  useEffect(() => {
    if (lang === "ENG") { setI18n(null); return; }
    client.get("/site/demo-i18n", { params: { lang } }).then((r) => setI18n(r.data)).catch(() => setI18n(null));
    setI(0); setPlaying(true);
  }, [lang]);

  // Localized storyline: SCRIPT = 6 onboarding + 26 storyline. Overlay translated h/d + section
  // labels while keeping rid/app/pane in code.
  const LSCRIPT = useMemo(() => {
    if (!i18n) return SCRIPT;
    const ob = i18n.onboarding, fs = i18n.fullsuite;
    if (!Array.isArray(ob) || ob.length !== OB || !Array.isArray(fs) || fs.length !== SCRIPT.length - OB) return SCRIPT;
    const storySecs = [...new Set(SCRIPT.slice(OB).map((s) => s.s))];
    const secMap = {};
    (i18n.sections || []).forEach((t, idx) => { if (storySecs[idx]) secMap[storySecs[idx]] = t; });
    return SCRIPT.map((s, k) => {
      if (k < OB) { const tr = ob[k]; return { ...s, h: tr.h || s.h, d: tr.d || s.d, s: i18n.me_section || s.s }; }
      const tr = fs[k - OB]; return { ...s, h: tr.h || s.h, d: tr.d || s.d, s: secMap[s.s] || s.s };
    });
  }, [i18n]);
  const ui = (i18n && i18n.ui) || {};
  const L = (k, fb) => ui[k] || fb;

  const step = LSCRIPT[i];
  const office = OFFICE_RIDS.includes(step.rid);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) { setBox(null); return; }
    const el = wrap.querySelector(`[data-rid="${step.rid}"]`);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect(); const w = wrap.getBoundingClientRect();
    setBox({ top: r.top - w.top, left: r.left - w.left, width: r.width, height: r.height });
  }, [step.rid]);

  useEffect(() => { measure(); const id = setInterval(measure, 400); window.addEventListener("resize", measure); return () => { clearInterval(id); window.removeEventListener("resize", measure); }; }, [measure]);
  useEffect(() => { if (activeRef.current) activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [i]);

  const advance = useCallback(() => setI((x) => (x + 1 < SCRIPT.length ? x + 1 : (setPlaying(false), x))), []);
  const narrText = step.silent ? "" : [step.h, step.d].filter(Boolean).join(". ");
  const prefetch = [1, 2, 3].map((d) => LSCRIPT[i + d]).filter(Boolean).map((s) => (s.silent ? "" : [s.h, s.d].filter(Boolean).join(". "))).filter(Boolean);
  const { loading: narrLoading } = useNarrator({ text: narrText, playing, voiceOn, lang, onEnded: advance, stepKey: i, prefetch });

  // Timer drives advance when voice is off, or for silent caveat steps even with voice on.
  useEffect(() => {
    if (!playing) return undefined;
    if (voiceOn && !step.silent) return undefined;
    const id = setTimeout(advance, step.dur || 6000);
    return () => clearTimeout(id);
  }, [playing, i, voiceOn, advance, step]);

  const go = (d) => { setPlaying(false); setI((x) => Math.min(Math.max(x + d, 0), SCRIPT.length - 1)); };

  const railIdx = RAIL.findIndex((r) => r.key === step.app);
  const onSetup = railIdx < 0; // Master Enabler onboarding — before any app node
  const railCenter = onSetup ? 0 : (railIdx + 0.5) / RAIL.length * 100;

  return (
    <div data-testid="full-suite-tour">
      <div className="grid gap-5 lg:grid-cols-2">
        {/* left — the app currently in focus */}
        <div>
          {/* suite rail — a comet glides between apps as the focus switches */}
          <div className="relative mb-4" data-testid="fs-suite-rail">
            <div className="pointer-events-none absolute left-[12.5%] right-[12.5%] top-3 h-0.5 rounded-full bg-black/10 dark:bg-white/10" />
            <div className="pointer-events-none absolute top-3 h-0.5 rounded-full bg-[var(--primary)] transition-all duration-700 ease-out" style={{ left: "12.5%", width: onSetup ? "0%" : `calc(${railCenter}% - 12.5%)` }} />
            <div className="pointer-events-none absolute top-3 z-20 -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-out" style={{ left: onSetup ? "5%" : `${railCenter}%`, opacity: onSetup ? 0.5 : 1 }}>
              <span className="block h-3 w-3 rounded-full bg-[var(--primary)]" style={{ boxShadow: "0 0 0 4px color-mix(in srgb, var(--primary) 22%, transparent)" }} />
            </div>
            <div className="relative z-10 grid grid-cols-4">
              {RAIL.map((r, k) => {
                const on = k === railIdx;
                return (
                  <div key={r.key} className="flex flex-col items-center gap-1">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full border transition-all duration-500 ${on ? "scale-110 border-[var(--primary)] bg-[var(--primary)] text-white shadow" : "border-black/15 bg-white text-black/50 dark:border-white/15 dark:bg-neutral-900 dark:text-white/50"}`}><r.icon size={12} /></span>
                    <span className={`text-[9px] transition-all ${on ? "font-semibold text-[var(--primary)]" : "opacity-50"}`}>{r.short}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-50">
            <Layers size={13} className="text-[var(--primary)]" /> {L("now_in", "Now in")}: <span className="text-[var(--primary)]" data-testid="fs-now-in-brand">{BRAND[step.app]}</span>
          </div>
          <div ref={wrapRef} className="relative">
            <PaneFor app={step.app} office={office} />
            {office && <div className="pointer-events-none absolute -top-2 left-1/2 z-40 -translate-x-1/2 rounded-full bg-[var(--primary)] px-3 py-0.5 text-[10px] font-semibold text-white shadow animate-in fade-in slide-in-from-top-1 duration-500" data-testid="fs-office-badge">Office Sync</div>}
            {box && <div className="pointer-events-none absolute z-20 rounded-md ring-4 ring-[var(--primary)] transition-all duration-500" style={{ top: box.top - 4, left: box.left - 4, width: box.width + 8, height: box.height + 8 }} data-testid="fs-ring" />}
            {box && <div className="pointer-events-none absolute z-30 transition-all duration-500" style={{ top: box.top + box.height - 4, left: box.left + box.width - 8 }} data-testid="fs-hand"><Hand size={26} className="rotate-[-18deg] fill-white text-[var(--primary)] drop-shadow-lg" /></div>}
          </div>
          <div className="mt-3 rounded-lg border border-[var(--primary)]/20 bg-[var(--primary)]/[0.04] p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]">{L("step", "Step")} {i + 1} / {SCRIPT.length} · {step.s}</div>
            <div className="mt-0.5 font-head text-base">{step.h}</div>
            <p className="mt-1 text-[13px] opacity-75">{step.d}</p>
          </div>
        </div>
        {/* right — the numbered storyline */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-50">{L("full_suite_walkthrough", "Full-suite walkthrough")}</div>
          <div className="max-h-[560px] overflow-y-auto rounded-xl border border-black/10 bg-white p-4 dark:border-white/10 dark:bg-neutral-900" data-testid="fs-manual">
            {LSCRIPT.map((sc, k) => {
              const active = k === i;
              const newSection = k === 0 || LSCRIPT[k - 1].s !== sc.s;
              return (
                <React.Fragment key={k}>
                  {newSection && <div className="mb-1 mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)] first:mt-0"><span className="rounded bg-[var(--primary)]/10 px-2 py-0.5">{BRAND[sc.app]}</span>{sc.s}</div>}
                  <button ref={active ? activeRef : null} onClick={() => { setPlaying(false); setI(k); }}
                    className={`mb-1.5 block w-full rounded-lg border px-3 py-2 text-start transition-all duration-300 ${active ? "border-[var(--primary)] bg-[var(--primary)]/[0.06]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`fs-step-${k}`}>
                    <div className="flex items-start gap-2 text-[13px] font-medium">
                      <span className={`mt-px shrink-0 rounded px-1.5 text-[11px] font-bold ${active ? "bg-[var(--primary)] text-white" : "bg-black/10 dark:bg-white/15"}`}>{k + 1}</span>
                      <span>{sc.h}</span>
                    </div>
                    {active && <p className="mt-1 ps-7 text-[12px] opacity-75">{sc.d}</p>}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {/* controls */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="fs-controls">
        <button onClick={() => go(-1)} disabled={i === 0} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift disabled:opacity-40 dark:border-white/15" data-testid="fs-prev"><SkipBack size={15} /> {L("prev", "Prev")}</button>
        <button onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white lift" data-testid="fs-playpause">{playing ? <><Pause size={15} /> {L("pause", "Pause")}</> : <><Play size={15} /> {L("play", "Play")}</>}</button>
        <button onClick={() => setVoiceOn((v) => !v)} title={voiceOn ? "Turn voice off" : "Turn voice narration on"} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm lift ${voiceOn ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-black/15 dark:border-white/15"}`} data-testid="fs-voice">{narrLoading && voiceOn ? <Loader2 size={15} className="animate-spin" /> : voiceOn ? <Volume2 size={15} /> : <VolumeX size={15} />} {L("voice", "Voice")}</button>
        <button onClick={() => go(1)} disabled={i === SCRIPT.length - 1} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift disabled:opacity-40 dark:border-white/15" data-testid="fs-next">{L("next", "Next")} <SkipForward size={15} /></button>
        <button onClick={() => { setI(0); setPlaying(true); }} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift dark:border-white/15" data-testid="fs-restart"><RotateCcw size={15} /> {L("restart", "Restart")}</button>
        <div className="ms-auto flex flex-wrap items-center gap-1">
          {SCRIPT.map((_, k) => <button key={k} onClick={() => { setPlaying(false); setI(k); }} className={`h-2 rounded-full transition-all ${k === i ? "w-6 bg-[var(--primary)]" : "w-2 bg-black/20 dark:bg-white/20"}`} data-testid={`fs-dot-${k}`} />)}
        </div>
      </div>
      <p className="mt-3 text-xs opacity-50" data-testid="fs-note">{L("note", "A guided, end-to-end tour of the full FopFo™ suite working as one office. Faithful replica screens — turn on Voice for narration in your language.")}</p>
    </div>
  );
}
